import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  Snackbar,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Security as SecurityIcon,
  Inventory as InventoryIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
  Person as PersonIcon,
  RequestPage as RequestIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';

const AccessProvisioningTab = () => {
  const { tokenManager, user } = useAuth();
  const [accessData, setAccessData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [rolesData, setRolesData] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');
  const [roleLoading, setRoleLoading] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);

  // Platform configurations
  const platforms = [
    {
      key: 'erp',
      label: 'ERP',
      fullName: 'Enterprise Resource Planning',
      icon: <BusinessIcon />,
      color: 'primary'
    },
    {
      key: 'gmos',
      label: 'GMOS',
      fullName: 'Marketplace for Online Sampling',
      icon: <SecurityIcon />,
      color: 'secondary'
    },
    {
      key: 'nonProjectExpense',
      label: 'Non Project Expense',
      fullName: 'Non Project Expense Management',
      icon: <InventoryIcon />,
      color: 'success'
    },
    {
      key : 'asset_tracker',
      label : 'Asset Tracker',
      fullName : 'Asset Management System',
      icon : <PersonIcon />,
      color : 'warning',
  }
  ];

  useEffect(() => {
    if (user?.id) {
      fetchAccessProvisioning();
    }
  }, [user?.id]);

  const fetchAccessProvisioning = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/access-provisioning/${user.id}`, {
        headers: {
          ...tokenManager.getAuthHeader(),
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setAccessData(result.data);
        } else {
          setError('Failed to fetch access provisioning data');
        }
      } else {
        setError('Failed to fetch access provisioning data');
      }
    } catch (error) {
      console.error('Error fetching access provisioning:', error);
      setError('Failed to fetch access provisioning data');
    } finally {
      setLoading(false);
    }
  };

  const handlePlatformClick = async (platform) => {
    if (!accessData || !accessData[platform.key] || !accessData.email) {
      setError('User data not available. Please try refreshing the page.');
      return;
    }

    // Handle GMOS - direct redirect without role selection
    if (platform.key === 'gmos') {
      try {
        const response = await fetch(`${API_URL}/access-provisioning/gmos-redirect`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...tokenManager.getAuthHeader(),
          },
          body: JSON.stringify({
            userId: user.id
          })
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            // Open GMOS in new tab
            window.open(result.data.redirectUrl, '_blank');
          } else {
            setError('Failed to get GMOS redirect URL');
          }
        } else {
          setError('Failed to get GMOS redirect URL');
        }
      } catch (error) {
        console.error('Error getting GMOS redirect URL:', error);
        setError('Failed to get GMOS redirect URL');
      }
      return;
    }

    // Handle Non Project Expense - direct redirect without role selection
    if (platform.key === 'nonProjectExpense') {
      try {
        const response = await fetch(`${API_URL}/access-provisioning/expense-redirect`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...tokenManager.getAuthHeader(),
          },
          body: JSON.stringify({
            userId: user.id
          })
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            // Open expense system in new tab
            window.open(result.data.redirectUrl, '_blank');
          } else {
            setError('Failed to get expense redirect URL');
          }
        } else {
          setError('Failed to get expense redirect URL');
        }
      } catch (error) {
        console.error('Error getting expense redirect URL:', error);
        setError('Failed to get expense redirect URL');
      }
      return;
    }  
    if (platform.key === 'asset_tracker') {
      try {
        const response = await fetch(`${API_URL}/access-provisioning/asset-tracker-redirect`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...tokenManager.getAuthHeader(),
          },
          body: JSON.stringify({
            userId: user.id
          })
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            // Open GMOS in new tab
            window.open(result.data.redirectUrl, '_blank');
          } else {
            setError('Failed to get Asset Tracker redirect URL');
          }
        } else {
          setError('Failed to get Asset Tracker redirect URL');
        }
      } catch (error) {
        console.error('Error getting Asset Tracker redirect URL:', error);
        setError('Failed to get Asset Tracker redirect URL');
      }
      return;
    }

    // Handle other platforms (ERP, Non Project Expense) with role selection
    setSelectedPlatform(platform);
    setRoleLoading(true);
    setSelectedRole('');

    try {
      // Get available roles from ERP API
      const response = await fetch(`${API_URL}/access-provisioning/get-roles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...tokenManager.getAuthHeader(),
        },
        body: JSON.stringify({
          email: accessData.email
        })
      });

      if (response.ok) {
        const result = await response.json();
        
        if (result.success) {
          // Check if there's only one role available
          const roles = result.data.role ? result.data.role.split(',') : [];
          
          if (roles.length === 1) {
            // Only one role available - redirect directly without dialog
            const singleRole = roles[0].trim();
            try {
              const redirectResponse = await fetch(`${API_URL}/access-provisioning/erp/verify-redirect`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  ...tokenManager.getAuthHeader(),
                },
                body: JSON.stringify({
                  email: accessData.email,
                  user_type: singleRole
                })
              });

              if (redirectResponse.ok) {
                const redirectResult = await redirectResponse.json();
                if (redirectResult.success) {
                  // Close loading and redirect to platform
                  setRoleLoading(false);
                  window.open(redirectResult.data.redirectUrl, '_blank');
                  return;
                }
              }
            } catch (redirectError) {
              console.error('Error getting redirect URL:', redirectError);
            }
          }
          
          // Multiple roles or redirect failed - show dialog
          setRolesData(result.data);
          setRoleDialogOpen(true);
        } else {
          setError(result.message || 'Failed to fetch roles from ERP');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.message || `Failed to fetch roles from ERP (Status: ${response.status})`);
      }
    } catch (error) {
      console.error('Error fetching ERP roles:', error);
      
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        setError('Network error: Unable to connect to ERP system. Please check your connection and try again.');
      } else {
        setError(`Failed to fetch roles from ERP: ${error.message}`);
      }
    } finally {
      setRoleLoading(false);
    }
  };

  const handleRoleSelection = (roleId) => {
    setSelectedRole(roleId);
  };

  const handleRoleSubmit = async () => {
    if (!selectedRole || !accessData?.email) {
      setError('Please select a role');
      return;
    }

    try {
      // Get redirect URL and open in new tab
      const response = await fetch(`${API_URL}/access-provisioning/erp/verify-redirect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...tokenManager.getAuthHeader(),
        },
        body: JSON.stringify({
          email: accessData.email,
          user_type: selectedRole
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // Close dialog and redirect to platform
          setRoleDialogOpen(false);
          setSelectedPlatform(null);
          setRolesData(null);
          setSelectedRole('');
          
          // Open the platform in new tab
          window.open(result.data.redirectUrl, '_blank');
        } else {
          setError('Failed to get redirect URL');
        }
      } else {
        setError('Failed to get redirect URL');
      }
    } catch (error) {
      console.error('Error getting redirect URL:', error);
      setError('Failed to get redirect URL');
    }
  };

  const handleCloseDialog = () => {
    setRoleDialogOpen(false);
    setSelectedPlatform(null);
    setRolesData(null);
    setSelectedRole('');
  };

  const handleRequestAccess = async (platformKey) => {
    if (!user?.id) {
      setError('User information not available');
      return;
    }

    if (!platformKey) {
      setError('Platform not specified');
      return;
    }

    const platform = platforms.find(p => p.key === platformKey);
    if (!platform) {
      setError('Invalid platform selected');
      return;
    }

    setRequestLoading(true);
    try {
      const response = await fetch(`${API_URL}/notifications/access-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...tokenManager.getAuthHeader(),
        },
        body: JSON.stringify({
          userId: user.id,
          platform: platform.key,
          platformName: platform.label,
          message: `Requesting access to ${platform.label} (${platform.fullName})`
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setRequestSuccess(true);
          setError(null);
        } else {
          setError(result.message || 'Failed to send access request');
        }
      } else {
        setError('Failed to send access request');
      }
    } catch (error) {
      console.error('Error sending access request:', error);
      setError('Failed to send access request');
    } finally {
      setRequestLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Error Alert - Show at top if there's an error */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        System Access Provisioning
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Click on the enabled platforms to access your accounts. Disabled platforms indicate access has not been provisioned yet.
      </Typography>


      {/* Platform Grid - Always show platforms, even if there's an error */}
      <Grid container spacing={3}>
        {platforms.map((platform) => {
          const isEnabled = accessData && accessData[platform.key];
          
          return (
            <Grid item xs={12} sm={6} md={4} key={platform.key}>
           <Card
  sx={{
    cursor: isEnabled ? "pointer" : "default",
    transition: "all 0.3s ease",
    "&:hover": isEnabled
      ? {
          transform: "translateY(-4px)",
          boxShadow: 4,
        }
      : {},
    border: isEnabled
      ? `2px solid ${platform.color}.main`
      : "1px solid #e0e0e0",
  }}
  onClick={() => isEnabled && handlePlatformClick(platform)}
>
  <CardContent sx={{ textAlign: "center", py: 3 }}>
    <Box
      sx={{
        color: isEnabled ? `${platform.color}.main` : "text.disabled",
        mb: 2,
        fontSize: "2rem",
        opacity: isEnabled ? 1 : 0.5, // dim only icon
      }}
    >
      {platform.icon}
    </Box>

    <Typography
      variant="h6"
      gutterBottom
      sx={{ fontWeight: 600, opacity: isEnabled ? 1 : 0.5 }}
    >
      {platform.label}
    </Typography>

    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ mb: 2, opacity: isEnabled ? 1 : 0.5 }}
    >
      {platform.fullName}
    </Typography>

    {/* Status */}
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 1,
        mb: 2,
        opacity: isEnabled ? 1 : 0.5,
      }}
    >
      {isEnabled ? (
        <>
          <CheckCircleIcon color="success" fontSize="small" />
          <Chip
            label="Access Granted"
            size="small"
            color="success"
            variant="outlined"
          />
        </>
      ) : (
        <>
          <BlockIcon color="disabled" fontSize="small" />
          <Chip
            label="Access Pending"
            size="small"
            color="default"
            variant="outlined"
          />
        </>
      )}
    </Box>
    {/* <br /> */}

    {/* Request button stays bright even if card is disabled */}
    {!isEnabled && (
      <Button
        variant="contained"
        color="primary"
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          handleRequestAccess(platform.key);
        }}
      >
        Request For Access 
      </Button>
    )}
  </CardContent>
</Card>

            </Grid>
          );
        })}
      </Grid>


      {/* No Access Data Message */}
      {!accessData && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" color="info" gutterBottom>
            Information
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Access provisioning data is not available yet. The platforms above show the available systems. Contact your administrator to request access.
          </Typography>
        </Box>
      )}

      {/* Summary */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
        <Typography variant="subtitle2" color="primary" gutterBottom>
          Access Summary
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {accessData ? Object.values(platforms).filter(p => accessData && accessData[p.key]).length : 0} of {platforms.length} platforms are accessible
        </Typography>
      </Box>

      {/* Role Selection Dialog */}
      <Dialog 
        open={roleDialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Select Your Role - {selectedPlatform?.label}
        </DialogTitle>
        <DialogContent>
          {roleLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : rolesData ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Please select your role for {selectedPlatform?.label}:
              </Typography>
              
              <FormControl>
                <RadioGroup
                  value={selectedRole}
                  onChange={(e) => handleRoleSelection(e.target.value)}
                >
                  {rolesData && rolesData.role ? 
                    rolesData.role.split(',').map((roleId, index) => {
                      const roleName = rolesData.role_names ? 
                        rolesData.role_names.split(',')[index]?.trim() : 
                        roleId;
                      return (
                        <FormControlLabel
                          key={roleId}
                          value={roleId}
                          control={<Radio />}
                          label={roleName}
                        />
                      );
                    })
                  : (
                    <Typography color="error">
                      No roles available
                    </Typography>
                  )}
                </RadioGroup>
              </FormControl>

              {selectedRole && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="info.main">
                    Selected: {rolesData && rolesData.role_names ? 
                      rolesData.role_names.split(',').find((_, index) => 
                        rolesData.role.split(',')[index] === selectedRole
                      )?.trim() : 
                      selectedRole
                    }
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Typography color="error">
              Failed to load roles
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleRoleSubmit}
            disabled={!selectedRole || roleLoading}
            variant="contained"
            color="primary"
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Notification */}
      <Snackbar
        open={requestSuccess}
        autoHideDuration={6000}
        onClose={() => setRequestSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setRequestSuccess(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          Access request sent successfully! Admin will be notified.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AccessProvisioningTab;
